import React, { useMemo, useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'flowbite-react';
import { FiTrendingUp, FiClock, FiTrash2, FiCheckCircle, FiAlertCircle, FiCircle, FiEdit, FiUsers } from 'react-icons/fi';
import * as wasm from 'sched';
import { StakeholderValueAllocation } from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { WorkspaceContext, useWorkspace } from '../../context/WorkspaceContext';
import { Avatar } from '../common/Avatar';
import ImprovementHelper from '../common/ImprovementHelper';

interface ValueAllocationCardProps {
  allocation: StakeholderValueAllocation;
  onRemove?: () => void;
}

interface TargetStatus {
  label: string;
  color: string; // tailwind text color
  icon: React.ReactNode;
}

interface Allocation {
  personName: string;
  hours: number;
  epochLabel: string;
  sortKey: string;
}

function formatEpoch(when: any): { label: string; sortKey: string } {
  if (when.type === 'Day') {
    return { label: when.day, sortKey: when.day };
  }
  if (when.type === 'Days') {
    return { label: `${when.start_inclusive} – ${when.end_inclusive}`, sortKey: when.start_inclusive };
  }
  return { label: 'Unknown', sortKey: '' };
}

const ValueAllocationCard: React.FC<ValueAllocationCardProps> = ({ allocation, onRemove }) => {
  const { database } = useDatabase();
  const { buildUrl, workspaceId } = useWorkspace();

  const target = allocation.target;
  const weight = allocation.weight;
  const targetType = (target as any).type;
  const targetId = (target as any).id;

  const { name, detailUrl, status, allocations } = useMemo(() => {
    if (!database) return { name: 'Unknown', detailUrl: '#', status: null as TargetStatus | null, allocations: [] as Allocation[] };

    let name = `${targetType} ${targetId}`;
    let detailUrl = '#';
    let status: TargetStatus | null = null;
    let allocations: Allocation[] = [];

    // Resolve people map once
    const people = database.people();
    const personName = (id: string) => people[id]?.name() ?? id.slice(0, 8);

    // Get allocations for a task id
    const getAllocationsForTask = (taskId: string) => {
      const raw = database.manual_allocations_for(taskId) as any[];
      if (!Array.isArray(raw)) return [];
      return raw.map((a: any) => {
        const { label, sortKey } = formatEpoch(a.when);
        return {
          personName: personName(a.personId),
          hours: Number(a.time),
          epochLabel: label,
          sortKey,
        };
      });
    };

    if (targetType === 'Task') {
      const tasks = database.tasks();
      const t = tasks[targetId];
      name = t ? t.title() : `Task ${targetId}`;
      detailUrl = buildUrl({ kind: 'task', taskId: targetId });
      if (t) {
        status = t.complete()
          ? { label: 'Complete', color: 'text-green-400', icon: <FiCheckCircle className="w-3.5 h-3.5" /> }
          : { label: 'Open', color: 'text-blue-400', icon: <FiCircle className="w-3.5 h-3.5" /> };
      }
      allocations = getAllocationsForTask(targetId);
    } else if (targetType === 'Outcome') {
      const outcomes = database.outcomes();
      const o = outcomes[targetId];
      name = o ? o.title() : `Outcome ${targetId}`;
      detailUrl = buildUrl({ kind: 'outcome', outcomeId: targetId });
      if (o) {
        // Outcome completion depends on criterion — we show draft status if relevant
        const criterion = o.completion_criterion();
        status = criterion.type === 'Manual' && (criterion as any).accepted_by
          ? { label: 'Accepted', color: 'text-green-400', icon: <FiCheckCircle className="w-3.5 h-3.5" /> }
          : { label: 'In progress', color: 'text-blue-400', icon: <FiCircle className="w-3.5 h-3.5" /> };
      }
    } else if (targetType === 'Problem') {
      const problems = database.problems();
      const p = problems[targetId];
      name = p ? p.title() : `Problem ${targetId}`;
      detailUrl = buildUrl({ kind: 'problem', problemId: targetId });
      if (p) {
        status = p.resolved()
          ? { label: 'Resolved', color: 'text-green-400', icon: <FiCheckCircle className="w-3.5 h-3.5" /> }
          : { label: 'Open', color: 'text-red-400', icon: <FiAlertCircle className="w-3.5 h-3.5" /> };
      }
    } else if (targetType === 'Project') {
      const projects = database.projects();
      const p = projects[targetId];
      name = p ? p.name() : `Product ${targetId}`;
      detailUrl = buildUrl({ kind: 'product', productId: targetId });
      if (p) {
        const stage = p.lifecycle_stage();
        status = stage
          ? { label: stage, color: 'text-cm-text-secondary', icon: <FiEdit className="w-3.5 h-3.5" /> }
          : { label: p.draft() ? 'Draft' : 'Active', color: 'text-blue-400', icon: <FiCircle className="w-3.5 h-3.5" /> };
      }
    }

    // Sort allocations by epoch (most recent first)
    allocations.sort((a, b) => b.sortKey.localeCompare(a.sortKey));

    return { name, detailUrl, status, allocations };
  }, [database, target, targetType, targetId, buildUrl]);

  // Resolve matched people/teams for this entity
  const { safeData } = useContext(WorkspaceContext);
  const [matchedActors, setMatchedActors] = useState<{ id: string; name: string; avatarUrl?: string; kind: 'Person' | 'Team' }[]>([]);

  useEffect(() => {
    if (!safeData || !database || !targetId) return;
    let cancelled = false;

    const fetchMatches = async () => {
      let matcher: wasm.NodeMatcher | null = null;
      if (targetType === 'Task') matcher = { TaskId: { id: targetId } } as any;
      else if (targetType === 'Outcome') matcher = { OutcomeId: { id: targetId } } as any;
      if (!matcher) return;

      try {
        const matches: any[] = await wasm.get_relevant_matches_for_object(safeData, matcher);
        if (cancelled) return;
        const people = database.people();
        const teams = database.teams();
        const actors = (matches ?? []).map((m: any) => {
          const actor = m.actor;
          if (actor.Person) {
            const p = people[actor.Person];
            return { id: actor.Person, name: p ? p.name() : actor.Person.slice(0, 8), avatarUrl: p?.avatar_url?.() ?? undefined, kind: 'Person' as const };
          }
          if (actor.Team) {
            const t = teams[actor.Team];
            return { id: actor.Team, name: t ? t.name() : actor.Team.slice(0, 8), kind: 'Team' as const };
          }
          return null;
        }).filter(Boolean) as any[];
        // Deduplicate by id
        const seen = new Set<string>();
        setMatchedActors(actors.filter(a => { if (seen.has(a.id)) return false; seen.add(a.id); return true; }));
      } catch (e) {
        console.error('Failed to fetch work matches:', e);
      }
    };

    fetchMatches();
    return () => { cancelled = true; };
  }, [safeData, database, targetType, targetId]);

  return (
    <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={detailUrl}
            className="font-medium text-cm-text-primary hover:text-cm-accent-orange transition-colors truncate"
          >
            {name}
          </Link>
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-cm-bg-elevated text-cm-text-muted shrink-0">
            {targetType}
          </span>
          <span className="text-xs font-mono text-cm-text-muted shrink-0">
            weight: {weight}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ImprovementHelper
            entityType={targetType.toLowerCase()}
            entityName={name}
            target={targetType === 'Task' || targetType === 'Outcome' ? { kind: targetType, id: targetId } : undefined}
          />
          {onRemove && (
            <button
              onClick={onRemove}
              className="p-1 text-cm-text-muted hover:text-red-400 transition-colors rounded"
              title="Remove allocation"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Status / Resources / Delivery row */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <h4 className="text-xs font-medium text-cm-text-muted uppercase tracking-wider mb-1.5">Status</h4>
          <div className="flex items-center justify-center h-10 rounded-lg bg-cm-bg-elevated">
            {status ? (
              <span className={`flex items-center gap-1.5 text-xs font-medium ${status.color}`}>
                {status.icon}
                {status.label}
              </span>
            ) : (
              <span className="text-xs text-cm-text-muted">Unknown</span>
            )}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-medium text-cm-text-muted uppercase tracking-wider mb-1.5">Resources</h4>
          <div className="flex items-center h-10 rounded-lg bg-cm-bg-elevated px-2">
            {matchedActors.length === 0 ? (
              <span className="text-xs text-cm-text-muted mx-auto">No matches</span>
            ) : matchedActors.length === 1 ? (
              <div className="flex items-center gap-2">
                {matchedActors[0].kind === 'Person' ? (
                  <Avatar imageUrl={matchedActors[0].avatarUrl} name={matchedActors[0].name} size="sm" showTooltip={false} />
                ) : (
                  <FiUsers className="w-5 h-5 text-cm-accent-teal" />
                )}
                <span className="text-xs text-cm-text-primary truncate">{matchedActors[0].name}</span>
              </div>
            ) : (
              <Tooltip
                content={
                  <div className="space-y-1">
                    {matchedActors.map(a => (
                      <div key={a.id} className="flex items-center gap-2 text-xs">
                        <span className={a.kind === 'Team' ? 'text-cm-accent-teal' : ''}>{a.kind === 'Team' ? '👥' : '👤'}</span>
                        <span>{a.name}</span>
                      </div>
                    ))}
                  </div>
                }
              >
                <div className="flex items-center gap-1.5 cursor-default">
                  <div className="flex -space-x-2">
                    {matchedActors.slice(0, 4).map((a) => (
                      <div key={a.id} className="relative">
                        {a.kind === 'Person' ? (
                          <Avatar imageUrl={a.avatarUrl} name={a.name} size="sm" showTooltip={false} />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-cm-accent-teal/20 border-2 border-cm-bg-surface flex items-center justify-center">
                            <FiUsers className="w-3.5 h-3.5 text-cm-accent-teal" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <span className="text-xs text-cm-text-secondary">{matchedActors.length}</span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-medium text-cm-text-muted uppercase tracking-wider mb-1.5">Delivery</h4>
          <div className="flex items-center justify-center h-10 border border-dashed border-cm-border-mid rounded-lg">
            <FiClock className="w-3.5 h-3.5 text-cm-text-muted mr-1" />
            <span className="text-xs text-cm-text-muted">No dates</span>
          </div>
        </div>
      </div>

      {/* Allocations */}
      <div>
        <h4 className="text-xs font-medium text-cm-text-muted uppercase tracking-wider mb-1.5">Allocations</h4>
        <div className="h-28 overflow-y-auto rounded-lg bg-cm-bg-elevated">
          {allocations.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-xs text-cm-text-muted">No allocations</span>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-cm-bg-elevated">
                <tr className="text-cm-text-muted text-left">
                  <th className="px-3 py-1.5 font-medium">When</th>
                  <th className="px-3 py-1.5 font-medium">Who</th>
                  <th className="px-3 py-1.5 font-medium text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((a, i) => (
                  <tr key={i} className="border-t border-cm-border-subtle">
                    <td className="px-3 py-1.5 text-cm-text-secondary font-mono">{a.epochLabel}</td>
                    <td className="px-3 py-1.5 text-cm-text-primary">{a.personName}</td>
                    <td className="px-3 py-1.5 text-cm-text-primary text-right font-mono">{a.hours}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValueAllocationCard;
